import { useEffect, useState } from "react";
import type { TopicFacade } from "@foundation/artemis-client";

export type UseArtemisClientOptions = {
  readonly serviceUrl?: string;
  readonly pollingInterval?: number;
  readonly topicOptions?: string;
};

const DEFAULT_POLL_INTERVAL = Number(
  process.env.NEXT_PUBLIC_ARTEMIS_POLL_INTERVAL ?? 5000
);

let clientPromise: Promise<TopicFacade> | null = null;

/** @internal Test-only — clears the singleton so each test starts fresh. */
export function resetArtemisClient(): void {
  clientPromise = null;
}

function isArtemisDisabled(): boolean {
  return "true" === process.env.NEXT_PUBLIC_DISABLE_ARTEMIS;
}

/**
 * Returns the Artemis real-time data client singleton, or `null` while
 * initializing / when disabled / on failure.
 *
 * @example
 * ```ts
 * function TickerPrice({ tickerDescription }: { tickerDescription: string }) {
 *   const artemisClient = useArtemisClient();
 *
 *   useEffect(() => {
 *     if (!artemisClient || !tickerDescription) return;
 *
 *     const unsubscribe = artemisClient.subscribe(
 *       "TICKER",
 *       tickerDescription,
 *       (data) => {
 *         // handle incoming real-time data
 *       }
 *     );
 *
 *     return () => unsubscribe();
 *   }, [artemisClient, tickerDescription]);
 * }
 * ```
 */
export function useArtemisClient(
  options?: UseArtemisClientOptions
): TopicFacade | null {
  const [client, setClient] = useState<TopicFacade | null>(null);

  const serviceUrl = options?.serviceUrl;
  const pollingInterval = options?.pollingInterval ?? DEFAULT_POLL_INTERVAL;
  const topicOptions = options?.topicOptions;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isArtemisDisabled()) return;

    if (clientPromise === null) {
      clientPromise = new Promise<TopicFacade>((resolve) => {
        window.artemis.cmd?.push(() => {
          resolve(
            window.getArtemisClient({
              // @ts-ignore
              topicOptions,
              pollingInterval,
              serviceUrl,
            })
          );
        });
      });
    }

    clientPromise
      .then((facade) => {
        setClient(facade);
      })
      .catch((e) => {
        clientPromise = null;
        console.log(e);
      });
    // pollingInterval only takes effect on first initialization
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return client;
}
